/**
 * DayStrip — the 0, unrolled, for screens too narrow to operate a ring.
 *
 * WHY IT EXISTS. The hero ring is a slider you drag around a circle. That is a
 * good control with a mouse and an acceptable one on a tablet; on a 320px phone
 * it is a 260px dial whose seven targets sit a thumb-width apart on a curve,
 * and the design record promised from the start that under the phone breakpoint
 * "the ring becomes a vertical day-strip, same data, same labels". Until now it
 * just shrank.
 *
 * SAME CONTROL, NOT A SECOND ONE. The strip renders the same `DayStop[]` from
 * zeroDay.ts, holds the same selected index, and receives the SAME slider
 * contract the ring carries — one `role="slider"`, the same arrow / Home / End
 * keys, the same `aria-valuetext`. Zero.tsx owns that state and hands the
 * bundle to whichever presentation is showing.
 *
 * ONLY ONE IS EVER PRESENT TO A USER. The swap is `display: none`, which
 * removes the hidden one from the accessibility tree AND from tab order — so a
 * keyboard user never finds two sliders for one thing, and a screen reader
 * never reads the day twice. That is the reason this is a CSS swap rather than
 * a `useResponsive()` branch: a JS breakpoint would render the wrong one for
 * the first paint on every phone, and the hero is the LCP element.
 *
 * The breakpoint is the repo's own (`media.tablet`, 768) rather than the design
 * record's 640 — src/styles/breakpoints.ts is the SSOT the rest of the app
 * agrees on, and inventing a fourth number to be 128px more precise would cost
 * more than it buys.
 */

import React from 'react';
import styled from 'styled-components';
import { media } from '../../styles/breakpoints';
import { WU_LABEL } from '../../config/workUnits';
import type { DayStop } from './zeroDay';

const Rail = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 30rem;
  display: flex;
  flex-direction: column;
  border-radius: 12px;

  &:focus-visible {
    outline: 2px solid var(--brand-gold);
    outline-offset: 4px;
  }

  /* The ring takes over from the tablet breakpoint up. */
  ${media.tablet} {
    display: none;
  }
`;

const Row = styled.li<{ $on: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: 3.4rem minmax(0, 1fr);
  align-items: start;
  gap: 0.75rem;
  padding: 0.7rem 0.25rem 0.7rem 0;
  cursor: pointer;
  border-top: 1px solid var(--brand-line);

  &:first-of-type {
    border-top: none;
  }

  /* The spine the stops sit on — the ring, cut open and stood upright. */
  &::before {
    content: '';
    position: absolute;
    left: 3.4rem;
    top: 0;
    bottom: 0;
    width: 1px;
    background: var(--brand-line);
    transform: translateX(-0.5rem);
  }
  &:first-of-type::before { top: 1.15rem; }
  &:last-of-type::before { bottom: calc(100% - 1.15rem); }
`;

const Hour = styled.span<{ $on: boolean }>`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 0.82rem;
  line-height: 1.5;
  color: ${(p) => (p.$on ? 'var(--brand-text)' : 'var(--brand-text-secondary)')};
  font-weight: ${(p) => (p.$on ? 700 : 400)};
  text-align: right;
  padding-right: 0.15rem;
`;

/** The stop itself, sitting on the spine — the ring's dot, kept. */
const Dot = styled.span<{ $on: boolean }>`
  position: absolute;
  left: 3.4rem;
  top: 1.05rem;
  width: ${(p) => (p.$on ? '11px' : '7px')};
  height: ${(p) => (p.$on ? '11px' : '7px')};
  border-radius: 50%;
  transform: translate(-50%, -50%) translateX(-0.5rem);
  background: ${(p) => (p.$on ? 'var(--brand-gold)' : 'var(--brand-canvas)')};
  border: 1.5px solid ${(p) => (p.$on ? 'var(--brand-gold)' : 'var(--brand-line-strong)')};
`;

const Body = styled.div`
  min-width: 0;
`;

const What = styled.div<{ $on: boolean }>`
  font-size: 0.9rem;
  line-height: 1.45;
  color: ${(p) => (p.$on ? 'var(--brand-text)' : 'var(--brand-text-secondary)')};
  font-weight: ${(p) => (p.$on ? 600 : 400)};
`;

const Cost = styled.div`
  margin-top: 0.25rem;
  font-size: 0.78rem;
  line-height: 1.5;
  color: var(--brand-text-secondary);
`;

const Free = styled.span`
  color: var(--brand-gold-text);
  font-weight: 700;
`;

export interface DayStripProps {
  stops: DayStop[];
  index: number;
  onPick: (i: number) => void;
  /** How the hour reads to a person — Zero.tsx owns the format. */
  clockLabel: (hour: number) => string;
  /**
   * The slider contract, built once by Zero.tsx and given to whichever
   * presentation is on screen. Typed loosely on purpose: it is a bundle of
   * ARIA attributes plus onKeyDown, and enumerating them here would be a
   * second place to keep them in step.
   */
  sliderProps: Record<string, unknown>;
}

export const DayStrip: React.FC<DayStripProps> = ({
  stops, index, onPick, clockLabel, sliderProps,
}) => (
  <Rail {...sliderProps} data-day-strip="">
    {stops.map((s, i) => {
      const on = i === index;
      return (
        <Row
          key={s.hour}
          $on={on}
          onClick={() => onPick(i)}
          data-day-stop={on ? 'current' : ''}
        >
          <Hour $on={on}>{clockLabel(s.hour)}</Hour>
          <Dot $on={on} aria-hidden="true" />
          <Body>
            <What $on={on}>{s.label}</What>
            {/* The price rides the SELECTED row only. Seven costs stacked in a
                phone-width column reads as a bill; one reads as the answer to
                "what did that cost?", which is the question the ring's readout
                answers on a wider screen. */}
            {on && (
              <Cost>
                {s.wu === 0 ? (
                  <><Free>free</Free> — variance is our risk, not your invoice</>
                ) : (
                  <>{s.wu} {WU_LABEL} {s.unit} — the published rate</>
                )}
              </Cost>
            )}
          </Body>
        </Row>
      );
    })}
  </Rail>
);

export default DayStrip;
